/**
 * Passport Experience Section
 * Manual work experience entries, sorted by start_date desc, nulls at bottom.
 */

import { useState } from 'react';
import { PassportSectionFallback } from './PassportSectionFallback';
import { parseDateOnly } from '@/lib/passport/dateOnly'; // Core-100 R4 W1: date-only facts in the local calendar
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Briefcase, Plus, Pencil, Trash2, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import {
  usePassportExperience, useCreateExperience, useUpdateExperience, useDeleteExperience,
} from '@/hooks/usePassportSections';

interface Props {
  userId: string;
  isOwner: boolean;
  isReadOnly?: boolean;
  /** Core-100 R2 CORE-D: a manager viewing someone else's Passport may attest an entry for their shop. */
  attestForShop?: { id: string; name?: string | null } | null;
}

/** Core-100 R4 CORE-D: the history columns of an entry (supersession and retraction keep what it once said). */
export interface EmploymentEntryHistory {
  provenance_class?: string | null;
  attested_shop_name?: string | null;
  attestation_withdrawn_at?: string | null;
  supersedes_id?: string | null;
  superseded_at?: string | null;
  retracted_at?: string | null;
  retraction_reason?: string | null;
  retracted_attestation?: { shop_name?: string | null } | null;
}

/** Core-100 R2/R4 CORE-D-GAP-001: what an employment entry rests on, in people's words. */
export function describeEmploymentProvenance(item: EmploymentEntryHistory): string {
  const base = (() => {
    if (item.provenance_class === 'employer_attested') return `Confirmed by ${item.attested_shop_name ?? 'the employer'}`;
    // OD-35: 'imported' is written only by a service import from another system, so it is information reported by an
    // external source — not a confirmation, and never "verified".
    if (item.provenance_class === 'imported') return 'Reported by External Source (imported)';
    if (item.retracted_at) {
      const shop = item.retracted_attestation?.shop_name ?? 'The employer';
      return `Self-reported (${shop} withdrew its confirmation${item.retraction_reason ? `: ${item.retraction_reason}` : ''})`;
    }
    if (item.supersedes_id) return 'Self-reported (corrects an earlier entry, which is kept)';
    return item.attestation_withdrawn_at ? 'Self-reported (edited after the employer confirmed it)' : 'Self-reported';
  })();
  return item.superseded_at ? `Earlier version, superseded by a correction · ${base}` : base;
}

/** Core-100 R4 CORE-D: an attested, retracted or superseded entry is part of the record and is not deleted. */
export function isEmploymentEntryErasable(item: EmploymentEntryHistory): boolean {
  return item.provenance_class !== 'employer_attested' && !item.retracted_at && !item.superseded_at;
}

interface FormData {
  employer_name: string;
  location: string;
  job_title: string;
  start_date: string;
  end_date: string;
  currently_here: boolean;
  description: string;
}

const emptyForm: FormData = {
  employer_name: '', location: '', job_title: '', start_date: '', end_date: '', currently_here: false, description: '',
};

export function PassportExperienceSection({ userId, isOwner, isReadOnly, attestForShop }: Props) {
  const [attestError, setAttestError] = useState<string | null>(null);
  const attest = async (experienceId: string) => {
    setAttestError(null);
    const { error } = await supabase.rpc('attest_employment' as never, { p_experience_id: experienceId, p_shop_id: attestForShop?.id } as never);
    if (error) {
      setAttestError(error.message.includes('NO_EMPLOYMENT_EVIDENCE') ? 'This person was never a member of your shop.' : 'Could not confirm this entry.');
      return;
    }
    void refetchExperience();
  };
  // Core-100 R4 CORE-D: the attesting shop withdraws its own attestation, with a reason; what it said is kept.
  const retract = async (experienceId: string) => {
    setAttestError(null);
    const reason = typeof window !== 'undefined' ? window.prompt('Why is your shop withdrawing this confirmation?') : null;
    if (!reason || !reason.trim()) return;
    const { error } = await supabase.rpc('retract_employment_attestation' as never, {
      p_experience_id: experienceId, p_shop_id: attestForShop?.id, p_reason: reason.trim(),
    } as never);
    if (error) { setAttestError('Could not withdraw this confirmation.'); return; }
    void refetchExperience();
  };
  // FIX-R41-MED-1: pass isOwner so non-owners get the public-safe column
  // subset (no `description` free-text). Consistent with references and
  // eligibility sections.
  const { data: items, isLoading, isError, refetch: refetchExperience } = usePassportExperience(userId, { isOwner });
  const createItem = useCreateExperience();
  const updateItem = useUpdateExperience();
  const deleteItem = useDeleteExperience();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  /** Core-100 R4 CORE-D: editing an attested entry is a SUPERSESSION (a new entry; the attested one is kept). */
  const [supersedingId, setSupersedingId] = useState<string | null>(null);
  const [supersedeError, setSupersedeError] = useState<string | null>(null);
  const [superseding, setSuperseding] = useState(false);
  const [form, setForm] = useState<FormData>(emptyForm);

  const openAdd = () => { setForm(emptyForm); setEditingId(null); setSupersedingId(null); setDialogOpen(true); };
  const openEdit = (item: any) => {
    setSupersedeError(null);
    setSupersedingId(item.provenance_class === 'employer_attested' ? item.id : null);
    setForm({
      employer_name: item.employer_name,
      location: item.location || '',
      job_title: item.job_title || '',
      start_date: item.start_date || '',
      end_date: item.end_date || '',
      currently_here: item.currently_here ?? false,
      description: item.description || '',
    });
    setEditingId(item.id);
    setDialogOpen(true);
  };

  const handleSave = () => {
    if (!form.employer_name.trim()) return;
    const payload = {
      employer_name: form.employer_name.trim(),
      location: form.location.trim() || undefined,
      job_title: form.job_title.trim() || undefined,
      start_date: form.start_date || undefined,
      end_date: form.currently_here ? undefined : (form.end_date || undefined),
      currently_here: form.currently_here,
      description: form.description.trim() || undefined,
    };
    if (supersedingId) {
      setSuperseding(true);
      void supabase.rpc('supersede_employment_entry' as never, {
        p_experience_id: supersedingId,
        p_employer_name: payload.employer_name,
        p_job_title: payload.job_title ?? null,
        p_start_date: payload.start_date ?? null,
        p_end_date: payload.end_date ?? null,
        p_currently_here: payload.currently_here,
        p_location: payload.location ?? null,
        p_description: payload.description ?? null,
      } as never).then(({ error }) => {
        setSuperseding(false);
        if (error) { setSupersedeError('Could not save the correction.'); return; }
        setDialogOpen(false);
        void refetchExperience();
      });
    } else if (editingId) {
      updateItem.mutate({ id: editingId, ...payload }, { onSuccess: () => setDialogOpen(false) });
    } else {
      createItem.mutate({ user_id: userId, ...payload }, { onSuccess: () => setDialogOpen(false) });
    }
  };

  const formatDateRange = (start?: string | null, end?: string | null, current?: boolean) => {
    const parts: string[] = [];
    if (start) parts.push(format(parseDateOnly(start), 'MMM yyyy'));
    if (current) parts.push('Present');
    else if (end) parts.push(format(parseDateOnly(end), 'MMM yyyy'));
    return parts.join(' – ') || null;
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Briefcase className="h-5 w-5 text-primary" />
            <CardTitle className="text-lg">Work Experience</CardTitle>
            {items && <Badge variant="secondary" className="text-xs">{items.length}</Badge>}
          </div>
          {isOwner && !isReadOnly && (
            <Button variant="outline" size="sm" onClick={openAdd}>
              <Plus className="h-4 w-4 mr-1" /> Add
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* CORE-C GAP-003: a failed read is not a statement that the person
            has none. Checked BEFORE the empty branch, which is where the
            failure used to land. */}
        {isLoading ? (
          <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : isError ? (
          <PassportSectionFallback sectionName="Experience" inline />
        ) : !items?.length ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            {isOwner ? 'No experience entries yet.' : 'No experience to display.'}
          </p>
        ) : (
          <>
          {attestError && <p className="text-xs text-destructive">{attestError}</p>}
          {items.map((item) => (
            <div key={item.id} className="border rounded-lg p-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-sm">{item.job_title || item.employer_name}</p>
                  {item.job_title && <p className="text-xs text-muted-foreground">{item.employer_name}</p>}
                  {item.location && <p className="text-xs text-muted-foreground">{item.location}</p>}
                  {(() => { const r = formatDateRange(item.start_date, item.end_date, item.currently_here); return r ? <p className="text-xs text-muted-foreground mt-0.5">{r}</p> : null; })()}
                  {item.description && <p className="text-xs text-muted-foreground mt-1">{item.description}</p>}
                  <p className="text-[11px] text-muted-foreground mt-1" data-testid="experience-provenance">{describeEmploymentProvenance(item as never)}</p>
                  {attestForShop && !isOwner && (item as EmploymentEntryHistory).provenance_class !== 'employer_attested'
                    && !(item as EmploymentEntryHistory).superseded_at && !(item as EmploymentEntryHistory).retracted_at && (
                    <Button variant="outline" size="sm" className="mt-1 h-7 text-xs" onClick={() => void attest(item.id)}>Confirm employment at {attestForShop.name ?? 'your shop'}</Button>
                  )}
                  {attestForShop && !isOwner && (item as EmploymentEntryHistory).provenance_class === 'employer_attested'
                    && (item as { attested_by_shop_id?: string | null }).attested_by_shop_id === attestForShop.id && (
                    <Button variant="outline" size="sm" className="mt-1 h-7 text-xs" data-testid="experience-retract" onClick={() => void retract(item.id)}>Withdraw your confirmation</Button>
                  )}
                </div>
                {isOwner && !isReadOnly && !(item as EmploymentEntryHistory).superseded_at && (
                  <div className="flex gap-1 flex-shrink-0">
                    <Button variant="ghost" size="icon" className="h-7 w-7" aria-label={(item as EmploymentEntryHistory).provenance_class === 'employer_attested' ? 'Correct entry' : 'Edit entry'} onClick={() => openEdit(item)}><Pencil className="h-3.5 w-3.5" /></Button>
                    {isEmploymentEntryErasable(item as EmploymentEntryHistory) && (
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" aria-label="Delete entry" onClick={() => deleteItem.mutate({ id: item.id, userId })}><Trash2 className="h-3.5 w-3.5" /></Button>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
          </>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{supersedingId ? 'Correct Experience' : editingId ? 'Edit Experience' : 'Add Experience'}</DialogTitle></DialogHeader>
          {supersedingId && (
            <p className="text-xs text-muted-foreground" data-testid="experience-supersede-note">
              Your employer confirmed this entry. Saving adds your corrected version; the confirmed entry stays on your record.
            </p>
          )}
          {supersedeError && <p className="text-xs text-destructive">{supersedeError}</p>}
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Employer Name *</Label>
              <Input value={form.employer_name} onChange={(e) => setForm({ ...form, employer_name: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Job Title</Label>
              <Input value={form.job_title} onChange={(e) => setForm({ ...form, job_title: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Location</Label>
              <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="City, State" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Start Date</Label>
                <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>End Date</Label>
                <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} disabled={form.currently_here} />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox id="currently_here" checked={form.currently_here} onCheckedChange={(c) => setForm({ ...form, currently_here: !!c })} />
              <Label htmlFor="currently_here" className="font-normal cursor-pointer">I currently work here</Label>
            </div>
            <div className="space-y-1.5">
              <Label>Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={!form.employer_name.trim() || createItem.isPending || updateItem.isPending || superseding}>
              {(createItem.isPending || updateItem.isPending || superseding) && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
